import React, { useEffect, useState } from 'react'
import {
  Dialog,
  DialogContent,
  Typography,
  Chip,
  Tooltip,
  Divider,
  Box,
} from '@mui/material'
import AttachFileIcon from '@mui/icons-material/AttachFile'
import MaleIcon from '@mui/icons-material/Male'
import FemaleIcon from '@mui/icons-material/Female'
import moment from 'moment'
import { EventEnum, PosologyPatientType, PrescriptionDto, PrescriptionStatus, ValidatePrescriptionDto } from '@services/dtos'
import { UpTransition } from '../animations'
import InputField from '../inputs/InputField'
import { yupResolver } from '@hookform/resolvers/yup'
import { prescriptionValidationSchema } from '@utils/schemas'
import { useForm } from 'react-hook-form'
import { FormHeader } from '../forms/FormHeader'
import { ChipPrescriptionStatus } from '../ChipPrescriptionStatus'
import { DataGridPro } from '@mui/x-data-grid-pro'
import { posologyColumns } from '@utils/gridColumns'
import { CompoundTypeLabels } from '@utils/constants'
import { isNil } from 'lodash'
import { useAppDispatch } from '@hooks/reduxHooks'
import { invalidatePrescription, validatePrescription } from '@state/reducers/prescriptionReducer'
import { enqueueSnackbar } from '@state/reducers/alertReducer'
import ConfirmDialog from './ConfirmDialog'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'

interface Props {
  open: boolean
  onClose: () => void
  prescription: PrescriptionDto
}

const getBmiCategory = (bmi: number): string => {
  if (bmi < 18.5) return 'Insuffisance pondérale'
  if (bmi < 25) return 'Corpulence normale'
  if (bmi < 30) return 'Surpoids'
  return 'Obésité'
}

export const PrescriptionDetailsDialog: React.FC<Props> = ({
  open,
  onClose,
  prescription,
}) => {
  const { control, handleSubmit, reset, watch } = useForm<ValidatePrescriptionDto>({
    resolver: yupResolver(prescriptionValidationSchema),
    defaultValues: {
      activityMBq: prescription.activityMBq || prescription.recommendedActivityMBq,
      notes: prescription.notes || '',
    }
  })
  const dispatch = useAppDispatch()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [pendingDto, setPendingDto] = useState<ValidatePrescriptionDto | null>(null)

  useEffect(() => {
    reset({ notes: prescription.notes || '', activityMBq: prescription.activityMBq || prescription.recommendedActivityMBq })
  }, [prescription.id, prescription.activityMBq])

  const { appointment, recommendedActivityMBq, compound, childWithAdultWeight } = prescription
  const { patient, exam, attachments, start } = appointment
  if (!patient) {
    return null
  }

  const age = moment(start).diff(moment(patient.birthDate), 'years')
  const isAdult = age >= 18
  const bmi =
    patient.weight != null && patient.height != null
      ? patient.weight / Math.pow(patient.height / 100, 2)
      : null
  const sexLabel = patient.sex === 'M' ? 'Homme' : patient.sex === 'F' ? 'Femme' : 'N/A'
  const SexIcon = patient.sex === 'M' ? MaleIcon : patient.sex === 'F' ? FemaleIcon : null
  const configExam = exam?.examCompounds?.find((examCompound) => examCompound.compound.id === compound.id)
  const posologySlices = configExam?.posologySlices ?? []

  const cb = async (msg: string) => {
    dispatch(
      enqueueSnackbar({
        message: msg,
        options: { variant: 'success' },
      }),
    )
  }

  const onRequestSubmit = (dto: ValidatePrescriptionDto) => {
    setPendingDto(dto)
    setConfirmOpen(true)
  }

  const onConfirmSubmit = () => {
    if (prescription.status === PrescriptionStatus.VALIDATED) {
      setConfirmOpen(false)
      setIsSubmitting(true)
      dispatch(invalidatePrescription({ id: prescription.id }))
        .unwrap()
        .then(() => {
          cb('La prescription a bien été invalidée')
          onClose()
        })
        .catch(() => cb('Une erreur est survenue'))
        .finally(() => setIsSubmitting(false))
      return
    }

    if (!pendingDto) {
      return
    }
    setConfirmOpen(false)
    setIsSubmitting(true)
    onSubmitForm(pendingDto)
  }

  const onSubmitForm = (dto: ValidatePrescriptionDto) => {
    dispatch(validatePrescription({ id: prescription.id, dto }))
      .unwrap()
      .then(() => {
        cb('La prescription a été validée avec succès')
        onClose()
      })
      .catch(() => cb('Une erreur est survenue'))
      .finally(() => setIsSubmitting(false))
  }

  const canSubmit = prescription.status !== PrescriptionStatus.PENDING || moment(start).startOf('day').diff(moment().startOf('day'), 'days') <= 7
  const tooEarlyMessage = !canSubmit
    ? `Validation possible à partir du ${moment(start).subtract(7, 'days').format('DD/MM/YYYY')} (7 jours avant le RDV)`
    : undefined

  return (
    <Dialog open={open} onClose={onClose} TransitionComponent={UpTransition} maxWidth="md" fullWidth sx={{ 
        '& .MuiDialog-paper': { 
          marginBottom: '80px'
        } 
      }}>
      <DialogContent className="p-0">
        <div className="flex flex-col">

          <FormHeader 
            title="Prescription"
            titleAdornment={
              prescription.validationHistory?.length && prescription.validationHistory.length > 0 ? (
                <Tooltip
                  title={
                    <div className="flex flex-col gap-1">
                      {prescription.validationHistory.map((log) => (
                        <span key={log.id}>
                          {log.event === EventEnum.PRESCRIPTION_VALIDATED ? 'Validée' : 'Invalidée'} par {log.createdBy?.firstName ?? ''} {log.createdBy?.lastName ?? ''}{' '}
                          le {moment(log.createdAt).format('DD/MM/YYYY')} à {moment(log.createdAt).format('HH:mm')}
                        </span>
                      ))}
                    </div>
                  }
                >
                  <span className="cursor-pointer">
                    <ChipPrescriptionStatus status={prescription.status} />
                  </span>
                </Tooltip>
              ) : (
                <ChipPrescriptionStatus status={prescription.status} />
              )
            }
            onClose={onClose}
            canSubmit={canSubmit}
            submitTooltip={tooEarlyMessage}
            isSubmitting={isSubmitting}
            subTitles={[`${exam?.label ?? 'Examen'} - ${moment(start).format('HH:mm DD/MM/YYYY')}`]}
            onSubmit={prescription.status === PrescriptionStatus.PENDING ? handleSubmit(onRequestSubmit) : () => setConfirmOpen(true)}
            submitText={prescription.status === PrescriptionStatus.PENDING ? 'Valider' : 'Invalider'}
          />

          <div className="flex flex-col md:flex-row min-h-0">

            <div className="flex flex-col gap-4 p-6 bg-gray-50 border-b md:border-b-0 md:border-r w-full md:w-[220px] md:min-w-[220px]">
              <Typography variant="subtitle2" color="text.secondary" className="uppercase tracking-wide text-xs">
                Patient
              </Typography>

              <div className="flex flex-row gap-x-6 md:flex-col md:gap-y-0">

                <div className="flex flex-col flex-1 md:flex-none">
                  <div>
                    <div className="flex items-center gap-1 mb-1">
                      {SexIcon && <SexIcon fontSize="small" color="action" />}
                      <Chip label={sexLabel} size="small" variant="outlined" />
                    </div>
                    <Typography variant="body1" fontWeight={600} lineHeight={1.3}>
                      {patient.firstName}
                    </Typography>
                    <Typography variant="body1" fontWeight={600} lineHeight={1.3} className="uppercase">
                      {patient.lastName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" className="mt-1 block font-mono">
                      SSN: {!patient.ssn ? '?' : patient.ssn}
                    </Typography>
                  </div>

                  <Divider className="!my-3" />

                  <div>
                    <Typography variant="caption" color="text.secondary">Âge (au moment du RDV)</Typography>
                    <Typography variant="body2" fontWeight={500} className="mt-1">
                      {age} ans {!isAdult && <Chip label="Mineur" size="small" color="warning" />}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Né{`${patient.sex === 'F' ? 'e' : ''}`} le {moment(patient.birthDate).format('DD/MM/YYYY')}
                    </Typography>
                  </div>
                </div>

                <Divider className="hidden md:block !my-3" />

                <div className="flex flex-col flex-1 md:flex-none">
                  <div>
                    <Typography variant="caption" color="text.secondary">Mesures</Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {isNil(patient.weight) ? '?' : patient.weight} kg
                      {childWithAdultWeight && (
                        <Chip label="Poids adulte" size="small" color="warning" className="ml-1" />
                      )}
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {isNil(patient.height) ? '?' : patient.height} cm
                    </Typography>
                  </div>
                  <div className="mt-2 pt-2 border-t border-gray-200">
                    <Typography variant="caption" color="text.secondary">IMC</Typography>
                    {bmi != null ? (
                      <>
                        <Typography variant="body2" fontWeight={500}>{bmi.toFixed(1)}</Typography>
                        {bmi >= 30 ? (
                          <Typography variant="body2" fontWeight={500}>
                            <Chip label={getBmiCategory(bmi)} size="small" color="warning" />
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="text.secondary">{getBmiCategory(bmi)}</Typography>
                        )}
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary">N/A</Typography>
                    )}
                  </div>
                </div>

              </div>
            </div>

            <div className="flex flex-col gap-6 p-6 flex-1 overflow-y-auto">

              {attachments && attachments.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {attachments.map((attachment) => (
                    <Tooltip key={attachment.id} title={`Consulter ${attachment.filename}`}>
                      <Chip
                        icon={<AttachFileIcon />}
                        label={attachment.filename}
                        variant="outlined"
                        clickable
                        onClick={() => {
                          // TODO: open files
                        }}
                      />
                    </Tooltip>
                  ))}
                </div>
              )}

              <div>
                <div className="flex flex-col gap-4">

                  <div className="rounded-lg p-4 border border-grey-100">
                    <div className="flex items-start">
                      <div className="flex-1 flex-1 flex flex-col gap-2">
                        <div>
                          <Typography variant="caption" color="text.secondary">MRP</Typography>
                          <Typography variant="body2" fontWeight={500}>{compound.name}</Typography>
                        </div>
                        <div>
                          <Typography variant="caption" color="text.secondary" display="block">Type</Typography>
                          <Typography variant="caption" color="text.secondary">{CompoundTypeLabels[compound.type]}</Typography>
                        </div>
                      </div>
                      <div className="flex-1 flex flex-col gap-2">
                        <div>
                          <Typography variant="caption" color="text.secondary">Trousse</Typography>
                          <Typography variant="body2" fontWeight={500}>{!compound.coldKit ? 'N/A' : compound.coldKit.name}</Typography>
                        </div>
                        <div>
                          <Typography variant="caption" color="text.secondary">Isotope{compound.isotopes?.length > 1 ? 's' : ''}</Typography>
                          <Typography variant="body2" fontWeight={500}>
                            {!compound.isotopes || compound.isotopes?.length === 0 ? 'N/A' : compound.isotopes.map(({ name }) => name).join(', ')}
                          </Typography>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                    <div className="flex items-center gap-2 mb-2">
                      <Typography 
                        variant="caption" 
                        color="text.secondary" 
                        sx={{ whiteSpace: 'nowrap', width: prescription.status === PrescriptionStatus.PENDING ? 154 : 126 }}>
                        Activité prescrite
                      </Typography>
                      {prescription.status === PrescriptionStatus.PENDING ? (<>
                        <InputField
                          control={control}
                          name="activityMBq"
                          type="number"
                          preventNegative
                          sx={{
                            '& .MuiInputBase-input': {
                              padding: '8px 12px',
                              fontSize: '1rem',
                            },
                          }}
                        />
                        <Typography variant="subtitle1" fontWeight={600} color="primary" sx={{ whiteSpace: 'nowrap' }}>
                          MBq
                        </Typography>
                      </>) : <Typography variant="subtitle1" fontWeight={600} color="primary" sx={{ whiteSpace: 'nowrap' }}>
                        {prescription.activityMBq} MBq
                      </Typography>}
                    </div>
                    <div className="flex items-center gap-2 mb-4">
                      <Typography variant="caption" color="text.secondary">Activité recommandée</Typography>
                      {recommendedActivityMBq ? (
                        <Typography 
                          variant={prescription.status === PrescriptionStatus.PENDING ? 'subtitle1' : 'caption'} 
                          fontWeight={prescription.status === PrescriptionStatus.PENDING ? 600 : 400} 
                          color={prescription.status === PrescriptionStatus.PENDING ? 'primary' : 'text.secondary'}>
                          {recommendedActivityMBq} MBq
                        </Typography>
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <WarningAmberIcon sx={{ fontSize: 14, color: 'warning.main' }} />
                          <Typography variant="caption" color="warning.main">
                            Aucune tranche reconnue au moment de la création du RDV
                          </Typography>
                        </Box>
                      )}
                    </div>
                    <DataGridPro
                      rows={posologySlices.filter(({ patientType }) => 
                        patientType === (isAdult || childWithAdultWeight ? PosologyPatientType.ADULT : PosologyPatientType.CHILD)
                      )}
                      columns={posologyColumns}
                      hideFooter
                      disableColumnMenu
                      autoHeight
                      density="compact"
                      sx={{
                        fontSize: '0.75rem',
                        '& .MuiDataGrid-row': { backgroundColor: 'transparent' },
                        '& .MuiDataGrid-row:hover': { backgroundColor: 'white' },
                      }}
                    />
                  </div>

                </div>
              </div>

              <div>
                <InputField
                  control={control}
                  name={'notes'}
                  label={'Commentaires'}
                  type={'text'}
                  multiline
                  rows={2}
                  disabled={prescription.status !== PrescriptionStatus.PENDING || isSubmitting}
                  sx={{
                    '& .MuiInputBase-input': {
                      padding: '4px !important',
                      fontSize: '0.875rem',
                    },
                    '& .MuiInputBase-input.Mui-disabled': {
                      WebkitTextFillColor: 'black',
                      opacity: 1,
                    },
                  }}
                />
              </div>
            </div>
          </div>

        </div>
      </DialogContent>

      <ConfirmDialog
        open={confirmOpen}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={onConfirmSubmit}
        title={`${prescription.status === PrescriptionStatus.PENDING ? 'Valider' : 'Invalider'} la prescription`}
        message={`Voulez-vous vraiment ${prescription.status === PrescriptionStatus.PENDING ? 'valider' : 'invalider'} 
          cette prescription à ${watch('activityMBq')} MBq ?${' '}
          ${prescription.status === PrescriptionStatus.VALIDATED ? ' Une nouvelle activité pourra être prescrite.' : ''}`}
      />
    </Dialog>
  )
}
